import { useEffect, useRef, useState } from "react";
import styled from "styled-components";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";

gsap.registerPlugin(ScrollTrigger);

const YT_ID = "1hRrR48vl9U";

const Section = styled.section`
  position: relative;
  background: #ffffff;
  padding: clamp(4rem, 10vw, 100px) 0;
  overflow: hidden;
`;

const Container = styled.div`
  max-width: 1100px;
  margin: 0 auto;
  padding: 0 1.5rem;
  position: relative;
  z-index: 2;
`;

const Label = styled.div`
  font-family: "Mendl", sans-serif;
  font-size: 0.7rem;
  letter-spacing: 0.28em;
  text-transform: uppercase;
  color: #a855f7;
  margin-bottom: 2rem;
  opacity: 0;
  transform: translateX(-30px);
`;

const Layout = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 3rem;
  align-items: center;
  margin-bottom: 4rem;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 2rem;
  }
`;

const Headline = styled.h2`
  font-family: "Mendl", sans-serif;
  font-size: clamp(2rem, 5vw, 3.5rem);
  font-weight: 300;
  line-height: 1.2;
  color: #3b3838;
  margin-bottom: 1.5rem;
  opacity: 0;
  transform: translateY(40px);

  em {
    overflow: visible;
    font-style: italic;
    font-weight: 600;
    background: linear-gradient(135deg, #e879f9, #a855f7);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
  }
`;

const BodyText = styled.p`
  font-family: "Mendl", sans-serif;
  font-size: 0.95rem;
  color: rgb(138, 136, 136);
  line-height: 1.7;
  margin-bottom: 1.2rem;
  opacity: 0;
  transform: translateY(25px);
`;

const Button = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 0 28px;
  height: 48px;
  border-radius: 100px;
  border: 1px solid rgba(168, 85, 247, 0.45);
  font-family: "Mendl", sans-serif;
  font-size: 0.8rem;
  text-decoration: none;
  color: rgb(90, 89, 89);
  opacity: 0;
  transform: translateY(20px);

  &:hover {
    border-color: rgba(232, 121, 249, 0.7);
    .arrow {
      transform: translateX(4px);
    }
  }

  .arrow {
    display: inline-block;
    transition: transform 0.25s ease;
  }
`;

const VideoContainer = styled.div`
  position: relative;
  width: 100%;
  aspect-ratio: 16/9;
  border-radius: 20px;
  overflow: hidden;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.3);
  opacity: 0;
  transform: translateX(40px);
  background: #000;
  cursor: pointer;
`;

const VideoFrame = styled.iframe`
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  border: 0;
  display: block;
`;

const Thumbnail = styled.img`
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
`;

const PlayButton = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 70px;
  height: 70px;
  background: rgba(168, 85, 247, 0.9);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.3s ease;
  z-index: 2;
  backdrop-filter: blur(4px);

  &:hover {
    transform: translate(-50%, -50%) scale(1.1);
    background: rgba(168, 85, 247, 1);
  }

  &::before {
    content: "▶";
    color: white;
    font-size: 1.8rem;
    margin-left: 4px;
  }
`;

const Overlay = styled.div`
  position: absolute;
  inset: 0;
  background: rgba(0, 0, 0, 0.3);
  transition: opacity 0.3s ease;
  pointer-events: none;
`;

const ValuesStrip = styled.div`
  display: flex;
  gap: 2rem;
  flex-wrap: wrap;
  border-top: 1px solid rgba(168, 85, 247, 0.1);
  padding-top: 3rem;
  opacity: 0;
  transform: translateY(30px);
`;

const Value = styled.div`
  display: flex;
  gap: 1rem;
  flex: 1;
  min-width: 180px;
`;

const ValueIcon = styled.div`
  font-size: 1.3rem;
`;

const ValueTitle = styled.div`
  font-size: 0.85rem;
  font-weight: 500;
  color: #1a1a1a;
  margin-bottom: 0.25rem;
`;

const ValueDesc = styled.div`
  font-size: 0.75rem;
  color: rgb(99, 95, 95);
  line-height: 1.5;
`;

const VALUES = [
  {
    icon: "🔥",
    title: "Radical Honesty",
    desc: "Real people showing up as themselves.",
  },
  {
    icon: "🔒",
    title: "Privacy by Design",
    desc: "Security built into every decision.",
  },
  {
    icon: "⚡",
    title: "Chemistry First",
    desc: "Built around instant attraction.",
  },
  { icon: "🌍", title: "Built for Everyone", desc: "You belong here, period." },
];

export default function AboutSection() {
  const sectionRef = useRef(null);
  const labelRef = useRef(null);
  const headlineRef = useRef(null);
  const bodyRefs = useRef([]);
  const ctaRef = useRef(null);
  const visualRef = useRef(null);
  const valuesRef = useRef(null);
  const [isPlaying, setIsPlaying] = useState(false);

  const handlePlayVideo = () => setIsPlaying(true);

  useEffect(() => {
    const ctx = gsap.context(() => {
      // Label
      gsap.to(labelRef.current, {
        scrollTrigger: { trigger: sectionRef.current, start: "top 80%" },
        opacity: 1,
        x: 0,
        duration: 0.6,
      });

      // Headline and body
      gsap.to(headlineRef.current, {
        scrollTrigger: { trigger: headlineRef.current, start: "top 85%" },
        opacity: 1,
        y: 0,
        duration: 0.6,
      });

      bodyRefs.current.forEach((ref, i) => {
        gsap.to(ref, {
          scrollTrigger: { trigger: ref, start: "top 85%" },
          opacity: 1,
          y: 0,
          duration: 0.5,
          delay: i * 0.1,
        });
      });

      // CTA, visual, values
      gsap.to(ctaRef.current, {
        scrollTrigger: { trigger: ctaRef.current, start: "top 85%" },
        opacity: 1,
        y: 0,
        duration: 0.5,
      });

      gsap.to(visualRef.current, {
        scrollTrigger: { trigger: visualRef.current, start: "top 85%" },
        opacity: 1,
        x: 0,
        duration: 0.7,
      });

      gsap.to(valuesRef.current, {
        scrollTrigger: { trigger: valuesRef.current, start: "top 85%" },
        opacity: 1,
        y: 0,
        duration: 0.6,
      });
    }, sectionRef);

    return () => ctx.revert();
  }, []);

  return (
    <Section ref={sectionRef} id="about">
      <Container>
        <Label ref={labelRef}>About Us</Label>

        <Layout>
          <div>
            <Headline ref={headlineRef}>
              We built the app
              <br />
              we <em>wished</em> existed.
            </Headline>

            <BodyText ref={(el) => (bodyRefs.current[0] = el)}>
              We were tired of apps that gamified love and treated your personal
              data like a product. So we started over — from a blank page.
            </BodyText>

            <BodyText ref={(el) => (bodyRefs.current[1] = el)}>
              Today we're a small team that believes great connections start
              with honesty. Chemistry can't be engineered — it can only be
              discovered.
            </BodyText>

            <Button ref={ctaRef} href="/about">
              Our full story
              <span className="arrow">→</span>
            </Button>
          </div>

          <VideoContainer
            ref={visualRef}
            onClick={isPlaying ? undefined : handlePlayVideo}
          >
            {isPlaying ? (
              <VideoFrame
                src={`https://www.youtube.com/embed/${YT_ID}?autoplay=1&rel=0`}
                title="NudR — our story"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                allowFullScreen
              />
            ) : (
              <>
                <Thumbnail
                  src={`https://img.youtube.com/vi/${YT_ID}/maxresdefault.jpg`}
                  alt=""
                />
                <PlayButton />
                <Overlay />
              </>
            )}
          </VideoContainer>
        </Layout>

        <ValuesStrip ref={valuesRef}>
          {VALUES.map((v, i) => (
            <Value key={i}>
              <ValueIcon>{v.icon}</ValueIcon>
              <div>
                <ValueTitle>{v.title}</ValueTitle>
                <ValueDesc>{v.desc}</ValueDesc>
              </div>
            </Value>
          ))}
        </ValuesStrip>
      </Container>
    </Section>
  );
}
